import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { fmtBRL, fmtBRLCompact, fmtPct } from '../../lib/format';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';

export default function KpiCard({
  label,
  value,
  format = 'currency', // 'currency' | 'percent' | 'number' | 'compact'
  delta,
  deltaLabel,
  sparkline,
  accent = '#D4AF37',
  icon: Icon,
  testid,
  highlight = false,
  onClick,
  pctOfRevenue, // opcional: número (ex. 12.5) mostrado como "12,5% da Receita"
}) {
  const display =
    format === 'currency' ? fmtBRL(value)
      : format === 'compact' ? fmtBRLCompact(value)
        : format === 'percent' ? fmtPct(value)
          : Number(value).toLocaleString('pt-BR');

  const isPositive = delta !== undefined && Number(delta) >= 0;
  const clickable = typeof onClick === 'function';

  return (
    <div
      className={`kpi-card relative p-5 ${highlight ? 'border-gold-glow' : ''} ${clickable ? 'cursor-pointer transition-colors hover:border-amber-700/40 hover:bg-zinc-900/50' : ''}`}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="overline text-zinc-500">{label}</span>
        {Icon && (
          <div
            className="w-7 h-7 rounded flex items-center justify-center"
            style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}
          >
            <Icon size={14} style={{ color: accent }} strokeWidth={2} />
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span
          className={`font-mono tabular-nums text-2xl font-semibold tracking-tight ${
            highlight ? 'gold-text' : 'text-zinc-50'
          }`}
          data-testid={testid ? `${testid}-value` : undefined}
        >
          {display}
        </span>
      </div>

      {pctOfRevenue !== undefined && pctOfRevenue !== null && (
        <div className="mt-1.5">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums"
            style={{ color: accent, background: `${accent}12`, border: `1px solid ${accent}30` }}
            data-testid={testid ? `${testid}-pct` : undefined}
            title="% em relação à Receita Total"
          >
            {fmtPct(pctOfRevenue)} da Receita
          </span>
        </div>
      )}

      {(delta !== undefined || deltaLabel) && (
        <div className="mt-2.5 flex items-center gap-2">
          {delta !== undefined && (
            <span className={`flex items-center gap-1 text-xs font-medium tabular-nums ${isPositive ? 'emerald-text' : 'red-text'}`}>
              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {fmtPct(Math.abs(Number(delta || 0)))}
            </span>
          )}
          {deltaLabel && <span className="text-xs text-zinc-500">{deltaLabel}</span>}
        </div>
      )}

      {sparkline && sparkline.length > 0 && (
        <div className="mt-3 h-10 -mx-1 sparkline">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline.map((v, i) => ({ i, v }))}>
              <defs>
                <linearGradient id={`grad-${testid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone" dataKey="v" stroke={accent}
                strokeWidth={1.5} fill={`url(#grad-${testid})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
